import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, RefreshCw } from 'lucide-react';
import ImageUploadButton from '@/components/ImageUploadButton';

const AdminContentNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);

  // Content states for each page
  const [homepageContent, setHomepageContent] = useState({
    company: {
      name: 'Speier Solarbetriebe',
      tagline: 'Ihr Partner für nachhaltige Energieinvestitionen'
    },
    hero: {
      title: 'Investieren Sie in die Energiezukunft Deutschlands',
      subtitle: 'Profitieren Sie von attraktiven Renditen durch nachhaltige Energie-Infrastrukturprojekte'
    },
    bess: {
      title: 'BESS Projekte',
      description: 'Battery Energy Storage Systems – Investieren Sie in die Zukunft der Energiespeicherung',
      cta: 'Projekte entdecken',
      badge1: '8-9% Rendite p.a.',
      badge2: '10 Jahre Laufzeit',
      badge3: 'Nachhaltig & Zukunftssicher',
      image: 'https://images.unsplash.com/photo-1578776349090-de61da00ff1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwb3dlciUyMHBsYW50fGVufDB8fHx8MTc2MDI1OTMwMXww&ixlib=rb-4.1.0&q=85'
    },
    pv: {
      title: 'PV-Anlagenverwaltung',
      description: 'Photovoltaik-Anlagenverwaltung – Professionelles Management Ihrer Solarinvestitionen',
      status: 'Demnächst verfügbar',
      badge1: 'Monitoring & Reporting',
      badge2: 'Wartungsmanagement',
      badge3: 'Ertragsoptimierung',
      image: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxyZW5ld2FibGUlMjBlbmVyZ3l8ZW58MHx8fHwxNzYwMjU5MzA2fDA&ixlib=rb-4.1.0&q=85'
    },
    features: {
      feature1_title: 'Nachhaltig',
      feature1_text: 'Investitionen in erneuerbare Energien und Energiespeicherung für Sicherheit für die nächste Generation',
      feature2_title: 'Sicher',
      feature2_text: 'Geprüfte Projekte mit transparenten Geschäftsmodellen und professioneller Verwaltung',
      feature3_title: 'Rentabel',
      feature3_text: 'Attraktive Renditen durch etablierte Technologien und langfristige Verträge'
    }
  });

  const [contactSettings, setContactSettings] = useState({
    company_name: '',
    general_email: '',
    phone: '',
    address: '',
    business_hours: '',
    marketing_email: '',
    project_confirmation_email: '',
    finance_email: '',
    service_email: '',
    facebook_url: '',
    linkedin_url: '',
    twitter_url: '',
    instagram_url: '',
    google_ads_id: '',
    google_ads_conversion_label: ''
  });

  const [projectEmailTemplates, setProjectEmailTemplates] = useState({});
  const [selectedProjectForEmail, setSelectedProjectForEmail] = useState('');
  const [projects, setProjects] = useState([]);

  const [impressumContent, setImpressumContent] = useState({
    company_name: '',
    company_form: '',
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    managing_director: '',
    registration_court: '',
    registration_number: '',
    vat_id: '',
    email: '',
    phone: '',
    responsible_person: '',
    additional_info: '',
    agb_download_url: ''
  });

  const [emailTemplates, setEmailTemplates] = useState({
    header_image: '',
    footer_image: '',
    registration: {
      subject: 'Willkommen - Bitte bestätigen Sie Ihre E-Mail',
      greeting: 'Willkommen bei Speier Solarbetriebe!',
      message: 'Vielen Dank für Ihre Registrierung. Bitte bestätigen Sie Ihre E-Mail-Adresse.'
    },
    interest_approved: {
      subject: 'Projekt für Sie freigeschaltet',
      greeting: 'Gute Nachrichten!',
      message: 'Ihr Interesse an einem Projekt wurde genehmigt. Sie können jetzt investieren.'
    },
    profit_distribution: {
      subject: 'Ertragausschüttung',
      greeting: 'Neue Ertragausschüttung',
      message: 'Es wurde ein Ertrag für Ihr Investment ausgeschüttet.'
    },
    investment_cancelled: {
      subject: 'Investition storniert',
      greeting: 'Investition storniert',
      message: 'Ihre Investition wurde storniert.'
    },
    password_reset: {
      subject: 'Passwort zurücksetzen',
      greeting: 'Passwort zurücksetzen',
      message: 'Sie haben eine Anfrage zum Zurücksetzen Ihres Passworts gestellt.'
    },
    user_created: {
      subject: 'Ihr Konto wurde erstellt',
      greeting: 'Willkommen!',
      message: 'Ein Administrator hat ein Konto für Sie erstellt.'
    }
  });

  const [bessInfoContent, setBessInfoContent] = useState({
    hero: {
      title: '',
      subtitle: ''
    },
    intro: {
      text: ''
    },
    benefits: {
      title: '',
      benefit1: '',
      benefit2: '',
      benefit3: '',
      benefit4: ''
    },
    investment: {
      title: '',
      text: ''
    }
  });

  const [infoPageContent, setInfoPageContent] = useState({
    hero: {
      title: '',
      subtitle: ''
    },
    steps: [
      { number: '1', title: '', text: '' },
      { number: '2', title: '', text: '' },
      { number: '3', title: '', text: '' },
      { number: '4', title: '', text: '' }
    ],
    downloads: {
      flyer_url: '',
      details_url: ''
    }
  });

  const [projectFormTexts, setProjectFormTexts] = useState({
    label_title: '',
    label_location: '',
    label_description: '',
    label_capacity_mw: '',
    label_expected_return: '',
    label_duration_years: '',
    label_total_shares: '',
    label_price_per_share: '',
    label_available_shares: '',
    label_status: '',
    section_payment_stages: '',
    label_stage_1: '',
    label_stage_2: '',
    label_stage_3: '',
    label_stage_4: '',
    label_stage_description: '',
    help_payment_stages: '',
    label_project_progress: '',
    help_project_progress: '',
    section_visibility: '',
    label_visibility: '',
    help_visibility: '',
    label_public: '',
    help_public: '',
    label_open_for_investments: '',
    help_open_for_investments: '',
    section_badge: '',
    label_badge_image_url: '',
    help_badge_image: '',
    label_badge_enabled: '',
    help_badge_enabled: '',
    section_images: '',
    label_main_image: '',
    label_additional_images: '',
    section_bank_details: '',
    label_iban: '',
    label_bic: '',
    label_account_holder: '',
    label_bank_name: '',
    label_purpose: '',
    help_purpose: '',
    section_project_details: '',
    label_site_plan: '',
    label_key_metrics: '',
    label_storage_technology: '',
    label_special_features: '',
    label_project_status_details: '',
    button_save: '',
    button_back: '',
    button_send_emails: '',
    msg_required_fields: '',
    msg_save_success: '',
    msg_save_error: ''
  });

  useEffect(() => {
    fetchAllContent();
  }, []);

  // Google Drive Link Converter
  const convertGoogleDriveLink = (url) => {
    if (!url) return url;
    
    // Check if it's a Google Drive link
    const driveRegex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const match = url.match(driveRegex);
    
    if (match) {
      const fileId = match[1];
      // Convert to direct thumbnail/image URL
      return `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
    }
    
    return url;
  };

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      // Fetch homepage content
      const homepageRes = await axios.get(`${API}/content/homepage`);
      if (homepageRes.data.content && Object.keys(homepageRes.data.content).length > 0) {
        const fetchedContent = homepageRes.data.content;
        
        // Convert Google Drive links for homepage images
        if (fetchedContent.bess?.image) {
          fetchedContent.bess.image = convertGoogleDriveLink(fetchedContent.bess.image);
        }
        if (fetchedContent.pv?.image) {
          fetchedContent.pv.image = convertGoogleDriveLink(fetchedContent.pv.image);
        }
        if (fetchedContent.hero?.bess_hero_image) {
          fetchedContent.hero.bess_hero_image = convertGoogleDriveLink(fetchedContent.hero.bess_hero_image);
        }
        if (fetchedContent.hero?.pv_hero_image) {
          fetchedContent.hero.pv_hero_image = convertGoogleDriveLink(fetchedContent.hero.pv_hero_image);
        }
        if (fetchedContent.features?.items) {
          fetchedContent.features.items = fetchedContent.features.items.map(item => {
            if (item.image) {
              return { ...item, image: convertGoogleDriveLink(item.image) };
            }
            return item;
          });
        }
        
        // Merge with default structure to ensure all fields exist
        setHomepageContent(prev => ({
          company: { ...prev.company, ...fetchedContent.company },
          hero: { ...prev.hero, ...fetchedContent.hero },
          bess: { ...prev.bess, ...fetchedContent.bess },
          pv: { ...prev.pv, ...fetchedContent.pv },
          features: { ...prev.features, ...fetchedContent.features }
        }));
      }

      // Fetch BESS info content
      const bessRes = await axios.get(`${API}/content/bess_info`);
      if (bessRes.data.content && Object.keys(bessRes.data.content).length > 0) {
        setBessInfoContent(bessRes.data.content);
      }

      // Fetch info page content
      const infoRes = await axios.get(`${API}/content/info_page`);
      if (infoRes.data.content && Object.keys(infoRes.data.content).length > 0) {
        setInfoPageContent(infoRes.data.content);
      }

      // Fetch email templates
      const emailRes = await axios.get(`${API}/content/email_templates`);
      if (emailRes.data.content && Object.keys(emailRes.data.content).length > 0) {
        setEmailTemplates(prev => ({
          ...prev,
          ...emailRes.data.content
        }));
      }

      // Fetch contact settings
      const contactRes = await axios.get(`${API}/settings/contact`);
      if (contactRes.data) {
        setContactSettings(contactRes.data);
      }

      // Fetch impressum content
      const impressumRes = await axios.get(`${API}/settings/impressum`);
      if (impressumRes.data) {
        setImpressumContent(impressumRes.data);
      }
      
      // Fetch projects for project email templates
      const projectsRes = await axios.get(`${API}/projects`);
      setProjects(projectsRes.data || []);
      
      // Fetch project email templates
      const projectEmailRes = await axios.get(`${API}/content/project_email_templates`);
      if (projectEmailRes.data && projectEmailRes.data.content) {
        setProjectEmailTemplates(projectEmailRes.data.content);
      }

      // Fetch project form texts
      const projectFormTextsRes = await axios.get(`${API}/settings/project-form-texts`);
      if (projectFormTextsRes.data) {
        setProjectFormTexts(projectFormTextsRes.data);
      }

    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Fehler beim Laden des Contents');
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeContent = async () => {
    if (!window.confirm('Standard-Content initialisieren? Dies überschreibt keine bestehenden Inhalte.')) {
      return;
    }

    setInitializing(true);
    try {
      const response = await axios.post(`${API}/content/initialize`);
      toast.success(`${response.data.message}\n${response.data.inserted} neue Inhalte erstellt`);
      fetchAllContent();
    } catch (error) {
      toast.error('Fehler beim Initialisieren');
    } finally {
      setInitializing(false);
    }
  };

  const saveContent = async (pageKey, content) => {
    setSaving(true);
    try {
      // Convert all Google Drive links before saving
      const convertedContent = JSON.parse(JSON.stringify(content)); // Deep clone
      
      // Convert BESS image if exists
      if (convertedContent.bess?.image) {
        convertedContent.bess.image = convertGoogleDriveLink(convertedContent.bess.image);
      }
      
      // Convert PV image if exists
      if (convertedContent.pv?.image) {
        convertedContent.pv.image = convertGoogleDriveLink(convertedContent.pv.image);
      }
      
      // Convert Hero images if exists
      if (convertedContent.hero?.bess_hero_image) {
        convertedContent.hero.bess_hero_image = convertGoogleDriveLink(convertedContent.hero.bess_hero_image);
      }
      if (convertedContent.hero?.pv_hero_image) {
        convertedContent.hero.pv_hero_image = convertGoogleDriveLink(convertedContent.hero.pv_hero_image);
      }
      
      // Convert feature images if they exist
      if (convertedContent.features?.items) {
        convertedContent.features.items = convertedContent.features.items.map(item => {
          if (item.image) {
            return { ...item, image: convertGoogleDriveLink(item.image) };
          }
          return item;
        });
      }
      
      await axios.put(`${API}/content/${pageKey}`, { content: convertedContent });
      toast.success('Content erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const saveContactSettings = async () => {
    setSaving(true);
    try {
      console.log('[DEBUG] Saving contact settings:', contactSettings);
      await axios.put(`${API}/settings/contact`, contactSettings);
      toast.success('Kontaktdaten erfolgreich gespeichert');
    } catch (error) {
      console.error('[DEBUG] Error saving contact settings:', error);
      toast.error('Fehler beim Speichern der Kontaktdaten');
    } finally {
      setSaving(false);
    }
  };

  const saveImpressum = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/impressum`, impressumContent);
      toast.success('Impressum erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern des Impressums');
    } finally {
      setSaving(false);
    }
  };

  const saveProjectFormTexts = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/project-form-texts`, projectFormTexts);
      toast.success('Projekt-Formular Texte erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Projekt-Formular Texte');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Button
              variant="outline"
              onClick={() => navigate('/bess/dashboard')}
              className="mb-4"
            >
              <ArrowLeft className="mr-2" size={18} />
              Zurück zum Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">Content Management</h1>
            <p className="text-slate-600 mt-2">Verwalten Sie alle Texte auf Ihrer Website</p>
          </div>
          <Button
            onClick={handleInitializeContent}
            disabled={initializing}
            variant="outline"
            className="border-blue-500 text-blue-600"
          >
            <RefreshCw className="mr-2" size={18} />
            Standard-Content initialisieren
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Lade Content...</p>
          </div>
        ) : (
          <Tabs defaultValue="homepage" className="space-y-6">
            <TabsList className="grid grid-cols-8 w-full">
              <TabsTrigger value="homepage">Homepage</TabsTrigger>
              <TabsTrigger value="bess_info">BESS Info</TabsTrigger>
              <TabsTrigger value="info_page">Info-Seite</TabsTrigger>
              <TabsTrigger value="email_templates">E-Mail-Vorlagen</TabsTrigger>
              <TabsTrigger value="project_emails">Projekt-Emails</TabsTrigger>
              <TabsTrigger value="project_form">Projekt-Formular</TabsTrigger>
              <TabsTrigger value="kontakt">Kontaktdaten</TabsTrigger>
              <TabsTrigger value="impressum">Impressum</TabsTrigger>
            </TabsList>

            {/* Homepage Content */}
            <TabsContent value="homepage">
              <Card>
                <CardHeader>
                  <CardTitle>Homepage Inhalte</CardTitle>
                  <CardDescription>Bearbeiten Sie die Texte auf der Startseite</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Company Info */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Firmeninformationen</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Firmenname</Label>
                        <Input
                          value={homepageContent.company?.name || ''}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            company: { ...homepageContent.company, name: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Tagline</Label>
                        <Input
                          value={homepageContent.company?.tagline || ''}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            company: { ...homepageContent.company, tagline: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Hero Section */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Hero-Bereich</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Hauptüberschrift</Label>
                        <Input
                          value={homepageContent.hero?.title || ''}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            hero: { ...homepageContent.hero, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Untertitel</Label>
                        <Textarea
                          value={homepageContent.hero?.subtitle || ''}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            hero: { ...homepageContent.hero, subtitle: e.target.value }
                          })}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* BESS Section */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">BESS Projekte Bereich</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Titel</Label>
                        <Input
                          value={homepageContent.bess?.title || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            bess: { ...homepageContent.bess, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Beschreibung</Label>
                        <Textarea
                          value={homepageContent.bess?.description || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            bess: { ...homepageContent.bess, description: e.target.value }
                          })}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>CTA Button Text</Label>
                        <Input
                          value={homepageContent.bess?.cta || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            bess: { ...homepageContent.bess, cta: e.target.value }
                          })}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Badge 1</Label>
                          <Input
                            value={homepageContent.bess?.badge1 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              bess: { ...homepageContent.bess, badge1: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <Label>Badge 2</Label>
                          <Input
                            value={homepageContent.bess?.badge2 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              bess: { ...homepageContent.bess, badge2: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <Label>Badge 3</Label>
                          <Input
                            value={homepageContent.bess?.badge3 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              bess: { ...homepageContent.bess, badge3: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                      
                      {/* BESS Image */}
                      <div>
                        <Label>🖼️ BESS Bild URL (Google Drive oder direkter Link)</Label>
                        <Input
                          value={homepageContent.bess?.image || ""}
                          onChange={(e) => {
                            const convertedUrl = convertGoogleDriveLink(e.target.value);
                            setHomepageContent({
                              ...homepageContent,
                              bess: { ...homepageContent.bess, image: convertedUrl }
                            });
                          }}
                          placeholder="Bild hochladen oder URL einfügen"
                        />
                        <ImageUploadButton
                          testId="bess-image-upload"
                          onUploaded={(url) => setHomepageContent(prev => ({ ...prev, bess: { ...prev.bess, image: url } }))}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          💡 Bild hochladen: es wird auf dem eigenen Server gespeichert (kein Abruf bei Google oder anderen Diensten)
                        </p>
                        {homepageContent.bess?.image && (
                          <div className="mt-2">
                            <img 
                              src={homepageContent.bess.image} 
                              alt="BESS Preview" 
                              className="w-32 h-20 object-cover rounded border"
                              onError={(e) => e.target.src = 'https://via.placeholder.com/128x80?text=Fehler'}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* PV Section */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">PV-Anlagenverwaltung Bereich</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Titel</Label>
                        <Input
                          value={homepageContent.pv?.title || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            pv: { ...homepageContent.pv, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Beschreibung</Label>
                        <Textarea
                          value={homepageContent.pv?.description || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            pv: { ...homepageContent.pv, description: e.target.value }
                          })}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Input
                          value={homepageContent.pv?.status || ""}
                          onChange={(e) => setHomepageContent({
                            ...homepageContent,
                            pv: { ...homepageContent.pv, status: e.target.value }
                          })}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Badge 1</Label>
                          <Input
                            value={homepageContent.pv?.badge1 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              pv: { ...homepageContent.pv, badge1: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <Label>Badge 2</Label>
                          <Input
                            value={homepageContent.pv?.badge2 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              pv: { ...homepageContent.pv, badge2: e.target.value }
                            })}
                          />
                        </div>
                        <div>
                          <Label>Badge 3</Label>
                          <Input
                            value={homepageContent.pv?.badge3 || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              pv: { ...homepageContent.pv, badge3: e.target.value }
                            })}
                          />
                        </div>
                      </div>
                      
                      {/* PV Image */}
                      <div>
                        <Label>🖼️ PV Bild URL (Google Drive oder direkter Link)</Label>
                        <Input
                          value={homepageContent.pv?.image || ""}
                          onChange={(e) => {
                            const convertedUrl = convertGoogleDriveLink(e.target.value);
                            setHomepageContent({
                              ...homepageContent,
                              pv: { ...homepageContent.pv, image: convertedUrl }
                            });
                          }}
                          placeholder="Bild hochladen oder URL einfügen"
                        />
                        <ImageUploadButton
                          testId="pv-image-upload"
                          onUploaded={(url) => setHomepageContent(prev => ({ ...prev, pv: { ...prev.pv, image: url } }))}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          💡 Bild hochladen: es wird auf dem eigenen Server gespeichert (kein Abruf bei Google oder anderen Diensten)
                        </p>
                        {homepageContent.pv?.image && (
                          <div className="mt-2">
                            <img 
                              src={homepageContent.pv.image} 
                              alt="PV Preview" 
                              className="w-32 h-20 object-cover rounded border"
                              onError={(e) => e.target.src = 'https://via.placeholder.com/128x80?text=Fehler'}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Features Section */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Features-Bereich</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Feature 1 */}
                        <div className="space-y-2">
                          <Label className="text-blue-600">Feature 1</Label>
                          <Input
                            placeholder="Titel"
                            value={homepageContent.features?.feature1_title || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature1_title: e.target.value }
                            })}
                          />
                          <Textarea
                            placeholder="Beschreibung"
                            value={homepageContent.features?.feature1_text || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature1_text: e.target.value }
                            })}
                            rows={3}
                          />
                        </div>

                        {/* Feature 2 */}
                        <div className="space-y-2">
                          <Label className="text-green-600">Feature 2</Label>
                          <Input
                            placeholder="Titel"
                            value={homepageContent.features?.feature2_title || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature2_title: e.target.value }
                            })}
                          />
                          <Textarea
                            placeholder="Beschreibung"
                            value={homepageContent.features?.feature2_text || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature2_text: e.target.value }
                            })}
                            rows={3}
                          />
                        </div>

                        {/* Feature 3 */}
                        <div className="space-y-2">
                          <Label className="text-purple-600">Feature 3</Label>
                          <Input
                            placeholder="Titel"
                            value={homepageContent.features?.feature3_title || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature3_title: e.target.value }
                            })}
                          />
                          <Textarea
                            placeholder="Beschreibung"
                            value={homepageContent.features?.feature3_text || ""}
                            onChange={(e) => setHomepageContent({
                              ...homepageContent,
                              features: { ...homepageContent.features, feature3_text: e.target.value }
                            })}
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => saveContent('homepage', homepageContent)}
                    disabled={saving}
                    className="w-full"
                  >
                    <Save className="mr-2" size={18} />
                    Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* BESS Info Content */}
            <TabsContent value="bess_info">
              <Card>
                <CardHeader>
                  <CardTitle>BESS Info Seite</CardTitle>
                  <CardDescription>Bearbeiten Sie die technischen Beschreibungen</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Hero */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Hero-Bereich</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Überschrift</Label>
                        <Input
                          value={bessInfoContent.hero.title}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            hero: { ...bessInfoContent.hero, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Untertitel</Label>
                        <Input
                          value={bessInfoContent.hero.subtitle}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            hero: { ...bessInfoContent.hero, subtitle: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Intro */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Einführungstext</h3>
                    <Textarea
                      value={bessInfoContent.intro.text}
                      onChange={(e) => setBessInfoContent({
                        ...bessInfoContent,
                        intro: { ...bessInfoContent.intro, text: e.target.value }
                      })}
                      rows={4}
                    />
                  </div>

                  {/* Benefits */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Vorteile</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Überschrift</Label>
                        <Input
                          value={bessInfoContent.benefits.title}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            benefits: { ...bessInfoContent.benefits, title: e.target.value }
                          })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          placeholder="Vorteil 1"
                          value={bessInfoContent.benefits.benefit1}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            benefits: { ...bessInfoContent.benefits, benefit1: e.target.value }
                          })}
                        />
                        <Input
                          placeholder="Vorteil 2"
                          value={bessInfoContent.benefits.benefit2}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            benefits: { ...bessInfoContent.benefits, benefit2: e.target.value }
                          })}
                        />
                        <Input
                          placeholder="Vorteil 3"
                          value={bessInfoContent.benefits.benefit3}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            benefits: { ...bessInfoContent.benefits, benefit3: e.target.value }
                          })}
                        />
                        <Input
                          placeholder="Vorteil 4"
                          value={bessInfoContent.benefits.benefit4}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            benefits: { ...bessInfoContent.benefits, benefit4: e.target.value }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Investment */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Investitionsmöglichkeiten</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Überschrift</Label>
                        <Input
                          value={bessInfoContent.investment.title}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            investment: { ...bessInfoContent.investment, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Beschreibung</Label>
                        <Textarea
                          value={bessInfoContent.investment.text}
                          onChange={(e) => setBessInfoContent({
                            ...bessInfoContent,
                            investment: { ...bessInfoContent.investment, text: e.target.value }
                          })}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => saveContent('bess_info', bessInfoContent)}
                    disabled={saving}
                    className="w-full"
                  >
                    <Save className="mr-2" size={18} />
                    Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Info Page Content */}
            <TabsContent value="info_page">
              <Card>
                <CardHeader>
                  <CardTitle>Info-Seite (Bubbles)</CardTitle>
                  <CardDescription>Bearbeiten Sie die 4-Schritte Anleitung mit Bubbles</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Hero */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Überschriften</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Hauptüberschrift</Label>
                        <Input
                          value={infoPageContent.hero?.title || ''}
                          onChange={(e) => setInfoPageContent({
                            ...infoPageContent,
                            hero: { ...infoPageContent.hero, title: e.target.value }
                          })}
                          placeholder="z.B. So funktioniert's"
                        />
                      </div>
                      <div>
                        <Label>Untertitel</Label>
                        <Input
                          value={infoPageContent.hero?.subtitle || ''}
                          onChange={(e) => setInfoPageContent({
                            ...infoPageContent,
                            hero: { ...infoPageContent.hero, subtitle: e.target.value }
                          })}
                          placeholder="z.B. Ihr Weg zur BESS-Investition..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Steps/Bubbles */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">4 Schritte (Bubbles)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {infoPageContent.steps && infoPageContent.steps.map((step, index) => (
                        <div key={index} className="p-4 border rounded-lg bg-slate-50">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold">
                              {step.number}
                            </div>
                            <Label className="font-semibold text-slate-700">Schritt {step.number}</Label>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs">Titel</Label>
                              <Input
                                value={step.title}
                                onChange={(e) => {
                                  const newSteps = [...infoPageContent.steps];
                                  newSteps[index].title = e.target.value;
                                  setInfoPageContent({ ...infoPageContent, steps: newSteps });
                                }}
                                placeholder={`z.B. ${index === 0 ? 'Registrieren' : index === 1 ? 'Projekt auswählen' : index === 2 ? 'Freischaltung' : 'Investieren'}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Beschreibung</Label>
                              <Textarea
                                value={step.text}
                                onChange={(e) => {
                                  const newSteps = [...infoPageContent.steps];
                                  newSteps[index].text = e.target.value;
                                  setInfoPageContent({ ...infoPageContent, steps: newSteps });
                                }}
                                rows={3}
                                placeholder="Beschreibung..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* PDF Downloads */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">PDF Downloads</h3>
                    <p className="text-sm text-slate-600 mb-4">Fügen Sie Links zu PDFs hinzu, die auf der Info-Seite heruntergeladen werden können</p>
                    <div className="space-y-4">
                      <div>
                        <Label>Flyer PDF URL</Label>
                        <Input
                          value={infoPageContent.downloads?.flyer_url || ''}
                          onChange={(e) => setInfoPageContent({
                            ...infoPageContent,
                            downloads: { ...infoPageContent.downloads, flyer_url: e.target.value }
                          })}
                          placeholder="z.B. https://example.com/flyer.pdf"
                        />
                        <p className="text-xs text-slate-500 mt-1">URL zum Flyer-PDF</p>
                      </div>
                      <div>
                        <Label>Details PDF URL</Label>
                        <Input
                          value={infoPageContent.downloads?.details_url || ''}
                          onChange={(e) => setInfoPageContent({
                            ...infoPageContent,
                            downloads: { ...infoPageContent.downloads, details_url: e.target.value }
                          })}
                          placeholder="z.B. https://example.com/details.pdf"
                        />
                        <p className="text-xs text-slate-500 mt-1">URL zum ausführlichen Detail-PDF</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => saveContent('info_page', infoPageContent)}
                    disabled={saving}
                    className="w-full"
                  >
                    <Save className="mr-2" size={18} />
                    Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* E-Mail Templates */}
            <TabsContent value="email_templates">
              <Card>
                <CardHeader>
                  <CardTitle>E-Mail-Vorlagen</CardTitle>
                  <CardDescription>Passen Sie die E-Mail-Texte an, die an Benutzer gesendet werden</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* SMTP Configuration */}
                  <div className="border-b pb-6 bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <span>📧</span> SMTP Server Konfiguration
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">E-Mail-Versand über Ihren SMTP-Server</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>SMTP Host</Label>
                          <Input
                            value={contactSettings.smtp_host}
                            onChange={(e) => setContactSettings({...contactSettings, smtp_host: e.target.value})}
                            placeholder="smtp.ionos.de"
                          />
                        </div>
                        <div>
                          <Label>SMTP Port</Label>
                          <Input
                            type="number"
                            value={contactSettings.smtp_port}
                            onChange={(e) => setContactSettings({...contactSettings, smtp_port: parseInt(e.target.value)})}
                            placeholder="587"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>SMTP Benutzername</Label>
                          <Input
                            value={contactSettings.smtp_username}
                            onChange={(e) => setContactSettings({...contactSettings, smtp_username: e.target.value})}
                            placeholder="email@domain.de"
                          />
                        </div>
                        <div>
                          <Label>SMTP Passwort</Label>
                          <Input
                            type="password"
                            value={contactSettings.smtp_password}
                            onChange={(e) => setContactSettings({...contactSettings, smtp_password: e.target.value})}
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Absender E-Mail</Label>
                          <Input
                            type="email"
                            value={contactSettings.smtp_from_email}
                            onChange={(e) => setContactSettings({...contactSettings, smtp_from_email: e.target.value})}
                            placeholder="noreply@domain.de"
                          />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={contactSettings.smtp_use_tls}
                              onChange={(e) => setContactSettings({...contactSettings, smtp_use_tls: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <span>TLS verwenden</span>
                          </Label>
                          <p className="text-xs text-slate-500 mt-1">Empfohlen für Port 587</p>
                        </div>
                      </div>
                      <Button onClick={saveContactSettings} disabled={saving} className="mt-4">
                        {saving ? 'Speichert...' : 'SMTP-Einstellungen speichern'}
                      </Button>
                    </div>
                  </div>

                  {/* Header/Footer Images */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">E-Mail Header & Footer Bilder</h3>
                    <p className="text-sm text-slate-600 mb-4">Diese Bilder erscheinen oben und unten in allen E-Mails</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Header Bild URL</Label>
                        <Input
                          value={emailTemplates.header_image || ''}
                          onChange={(e) => setEmailTemplates({...emailTemplates, header_image: e.target.value})}
                          placeholder="https://example.com/header.png"
                        />
                        <p className="text-xs text-slate-500 mt-1">Empfohlene Größe: 600x150px</p>
                      </div>
                      <div>
                        <Label>Footer Bild URL</Label>
                        <Input
                          value={emailTemplates.footer_image || ''}
                          onChange={(e) => setEmailTemplates({...emailTemplates, footer_image: e.target.value})}
                          placeholder="https://example.com/footer.png"
                        />
                        <p className="text-xs text-slate-500 mt-1">Empfohlene Größe: 600x100px</p>
                      </div>
                    </div>
                  </div>

                  {/* Registration Email */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">📧 Registrierungs-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.registration?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            registration: {...emailTemplates.registration, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.registration?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            registration: {...emailTemplates.registration, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.registration?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            registration: {...emailTemplates.registration, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{email}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{verification_link}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Interest Approved Email */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">✅ Interesse Genehmigt-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.interest_approved?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            interest_approved: {...emailTemplates.interest_approved, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.interest_approved?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            interest_approved: {...emailTemplates.interest_approved, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.interest_approved?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            interest_approved: {...emailTemplates.interest_approved, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{project_title}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{dashboard_link}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Profit Distribution Email */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">💰 Ertragausschüttungs-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.profit_distribution?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            profit_distribution: {...emailTemplates.profit_distribution, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.profit_distribution?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            profit_distribution: {...emailTemplates.profit_distribution, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.profit_distribution?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            profit_distribution: {...emailTemplates.profit_distribution, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{amount}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{project_title}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{shares}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{dashboard_link}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Investment Cancelled Email */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">🚫 Investition Storniert-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.investment_cancelled?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            investment_cancelled: {...emailTemplates.investment_cancelled, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.investment_cancelled?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            investment_cancelled: {...emailTemplates.investment_cancelled, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.investment_cancelled?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            investment_cancelled: {...emailTemplates.investment_cancelled, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{project_title}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{shares}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{reason}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Password Reset Email */}
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">🔑 Passwort Zurücksetzen-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.password_reset?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            password_reset: {...emailTemplates.password_reset, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.password_reset?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            password_reset: {...emailTemplates.password_reset, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.password_reset?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            password_reset: {...emailTemplates.password_reset, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{reset_link}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{expiry_time}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* User Created Email */}
                  <div className="pb-6">
                    <h3 className="text-lg font-semibold mb-4">👤 Benutzer Erstellt-E-Mail</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Betreff</Label>
                        <Input
                          value={emailTemplates.user_created?.subject || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            user_created: {...emailTemplates.user_created, subject: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Begrüßung</Label>
                        <Input
                          value={emailTemplates.user_created?.greeting || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            user_created: {...emailTemplates.user_created, greeting: e.target.value}
                          })}
                        />
                      </div>
                      <div>
                        <Label>Nachricht</Label>
                        <Textarea
                          value={emailTemplates.user_created?.message || ''}
                          onChange={(e) => setEmailTemplates({
                            ...emailTemplates,
                            user_created: {...emailTemplates.user_created, message: e.target.value}
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          📝 Verfügbare Platzhalter: <code className="bg-slate-100 px-1 rounded">{'{username}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{email}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{password}'}</code>, <code className="bg-slate-100 px-1 rounded">{'{login_link}'}</code>
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button onClick={() => saveContent('email_templates', emailTemplates)} disabled={saving}>
                    {saving ? 'Speichert...' : 'E-Mail-Vorlagen speichern'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Project Email Templates Tab */}
            <TabsContent value="project_emails">
              <Card>
                <CardHeader>
                  <CardTitle>📧 Projekt Email-Vorlagen</CardTitle>
                  <CardDescription>
                    Verwalten Sie Zahlungserinnerungen für jedes Projekt (4 Stufen pro Projekt)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Project Selector */}
                  <div>
                    <Label htmlFor="projectSelector">Projekt auswählen</Label>
                    <select
                      id="projectSelector"
                      value={selectedProjectForEmail}
                      onChange={(e) => setSelectedProjectForEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    >
                      <option value="">-- Projekt wählen --</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedProjectForEmail && (
                    <>
                      {/* Stage 1 Email */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold mb-4 text-yellow-700">📬 Stufe 1 - Zahlungserinnerung</h3>
                        <div className="space-y-4">
                          <div>
                            <Label>Betreff</Label>
                            <Input
                              value={projectEmailTemplates[selectedProjectForEmail]?.stage_1?.subject || ''}
                              onChange={(e) => setProjectEmailTemplates({
                                ...projectEmailTemplates,
                                [selectedProjectForEmail]: {
                                  ...projectEmailTemplates[selectedProjectForEmail],
                                  stage_1: {
                                    ...projectEmailTemplates[selectedProjectForEmail]?.stage_1,
                                    subject: e.target.value
                                  }
                                }
                              })}
                              placeholder="Zahlungsaufforderung - Stufe 1"
                            />
                          </div>
                          <div>
                            <Label>Begrüßung</Label>
                            <Input
                              value={projectEmailTemplates[selectedProjectForEmail]?.stage_1?.greeting || ''}
                              onChange={(e) => setProjectEmailTemplates({
                                ...projectEmailTemplates,
                                [selectedProjectForEmail]: {
                                  ...projectEmailTemplates[selectedProjectForEmail],
                                  stage_1: {
                                    ...projectEmailTemplates[selectedProjectForEmail]?.stage_1,
                                    greeting: e.target.value
                                  }
                                }
                              })}
                              placeholder="Erste Zahlung erforderlich"
                            />
                          </div>
                          <div>
                            <Label>Nachricht</Label>
                            <textarea
                              value={projectEmailTemplates[selectedProjectForEmail]?.stage_1?.message || ''}
                              onChange={(e) => setProjectEmailTemplates({
                                ...projectEmailTemplates,
                                [selectedProjectForEmail]: {
                                  ...projectEmailTemplates[selectedProjectForEmail],
                                  stage_1: {
                                    ...projectEmailTemplates[selectedProjectForEmail]?.stage_1,
                                    message: e.target.value
                                  }
                                }
                              })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md min-h-[100px]"
                              placeholder="Ihr Text hier..."
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Platzhalter: {'{username}'}, {'{project_name}'}, {'{required_amount}'}, {'{still_to_pay}'}, {'{stage}'}, {'{project_progress}'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Similar blocks for Stage 2, 3, 4 */}
                      {[2, 3, 4].map(stage => (
                        <div key={stage} className="border-t pt-6">
                          <h3 className="text-lg font-semibold mb-4 text-yellow-700">📬 Stufe {stage} - Zahlungserinnerung</h3>
                          <div className="space-y-4">
                            <div>
                              <Label>Betreff</Label>
                              <Input
                                value={projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`]?.subject || ''}
                                onChange={(e) => setProjectEmailTemplates({
                                  ...projectEmailTemplates,
                                  [selectedProjectForEmail]: {
                                    ...projectEmailTemplates[selectedProjectForEmail],
                                    [`stage_${stage}`]: {
                                      ...projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`],
                                      subject: e.target.value
                                    }
                                  }
                                })}
                                placeholder={`Zahlungsaufforderung - Stufe ${stage}`}
                              />
                            </div>
                            <div>
                              <Label>Begrüßung</Label>
                              <Input
                                value={projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`]?.greeting || ''}
                                onChange={(e) => setProjectEmailTemplates({
                                  ...projectEmailTemplates,
                                  [selectedProjectForEmail]: {
                                    ...projectEmailTemplates[selectedProjectForEmail],
                                    [`stage_${stage}`]: {
                                      ...projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`],
                                      greeting: e.target.value
                                    }
                                  }
                                })}
                                placeholder={`Stufe ${stage} Zahlungserinnerung`}
                              />
                            </div>
                            <div>
                              <Label>Nachricht</Label>
                              <textarea
                                value={projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`]?.message || ''}
                                onChange={(e) => setProjectEmailTemplates({
                                  ...projectEmailTemplates,
                                  [selectedProjectForEmail]: {
                                    ...projectEmailTemplates[selectedProjectForEmail],
                                    [`stage_${stage}`]: {
                                      ...projectEmailTemplates[selectedProjectForEmail]?.[`stage_${stage}`],
                                      message: e.target.value
                                    }
                                  }
                                })}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md min-h-[100px]"
                                placeholder="Ihr Text hier..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Save Button */}
                      <Button
                        onClick={() => saveContent('project_email_templates', projectEmailTemplates)}
                        disabled={saving}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        {saving ? 'Speichere...' : '💾 Vorlagen speichern'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Kontaktdaten Tab */}
            <TabsContent value="kontakt">
              <Card>
                <CardHeader>
                  <CardTitle>Kontaktdaten & Tracking</CardTitle>
                  <CardDescription>Verwalten Sie Firmendaten, E-Mails und Google Ads</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Firmenname</Label>
                      <Input value={contactSettings.company_name || ''} onChange={(e) => setContactSettings({...contactSettings, company_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Allgemeine E-Mail</Label>
                      <Input value={contactSettings.general_email || ''} onChange={(e) => setContactSettings({...contactSettings, general_email: e.target.value})} />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input value={contactSettings.phone || ''} onChange={(e) => setContactSettings({...contactSettings, phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Adresse</Label>
                      <Input value={contactSettings.address || ''} onChange={(e) => setContactSettings({...contactSettings, address: e.target.value})} />
                    </div>
                  </div>

                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold mb-4">E-Mail Adressen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Marketing E-Mail</Label>
                        <Input value={contactSettings.marketing_email || ''} onChange={(e) => setContactSettings({...contactSettings, marketing_email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Projektbestätigung E-Mail</Label>
                        <Input value={contactSettings.project_confirmation_email || ''} onChange={(e) => setContactSettings({...contactSettings, project_confirmation_email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Finanzen E-Mail</Label>
                        <Input value={contactSettings.finance_email || ''} onChange={(e) => setContactSettings({...contactSettings, finance_email: e.target.value})} />
                      </div>
                      <div>
                        <Label>Service E-Mail</Label>
                        <Input value={contactSettings.service_email || ''} onChange={(e) => setContactSettings({...contactSettings, service_email: e.target.value})} />
                      </div>
                    </div>
                  </div>

                  {/* E-Mail Textbausteine ENTFERNT - Bitte verwenden Sie den Tab "E-Mail-Vorlagen" für vollständige E-Mail-Verwaltung */}
                  <div className="border-t pt-6 mt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Hinweis: E-Mail-Vorlagen
                      </h4>
                      <p className="text-sm text-blue-800">
                        E-Mail-Vorlagen werden jetzt im separaten Tab <strong>"E-Mail-Vorlagen"</strong> verwaltet. 
                        Dort können Sie vollständige E-Mail-Texte mit Betreff, Begrüßung und Nachricht bearbeiten.
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      SMTP Server Konfiguration (IONOS E-Mail)
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                      Konfigurieren Sie Ihren IONOS E-Mail Server für automatische E-Mails (Registrierung, Benachrichtigungen)
                    </p>
                    <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">SMTP Host</Label>
                          <Input 
                            value={contactSettings.smtp_host || 'smtp.ionos.de'} 
                            onChange={(e) => setContactSettings({...contactSettings, smtp_host: e.target.value})} 
                            placeholder="z.B. smtp.ionos.de" 
                          />
                          <p className="text-xs text-slate-500 mt-1">Standard für IONOS: smtp.ionos.de</p>
                        </div>
                        <div>
                          <Label className="font-semibold">SMTP Port</Label>
                          <Input 
                            type="number"
                            value={contactSettings.smtp_port || 587} 
                            onChange={(e) => setContactSettings({...contactSettings, smtp_port: parseInt(e.target.value)})} 
                            placeholder="587" 
                          />
                          <p className="text-xs text-slate-500 mt-1">Standard: 587 (TLS) oder 465 (SSL)</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">E-Mail Adresse (Benutzername)</Label>
                          <Input 
                            type="email"
                            value={contactSettings.smtp_username || ''} 
                            onChange={(e) => setContactSettings({...contactSettings, smtp_username: e.target.value})} 
                            placeholder="ihre-email@ihre-domain.de" 
                          />
                          <p className="text-xs text-slate-500 mt-1">Ihre vollständige IONOS E-Mail-Adresse</p>
                        </div>
                        <div>
                          <Label className="font-semibold">Passwort</Label>
                          <Input 
                            type="password"
                            value={contactSettings.smtp_password || ''} 
                            onChange={(e) => setContactSettings({...contactSettings, smtp_password: e.target.value})} 
                            placeholder="••••••••" 
                          />
                          <p className="text-xs text-slate-500 mt-1">Ihr IONOS E-Mail-Passwort</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">Absender E-Mail</Label>
                          <Input 
                            type="email"
                            value={contactSettings.smtp_from_email || ''} 
                            onChange={(e) => setContactSettings({...contactSettings, smtp_from_email: e.target.value})} 
                            placeholder="noreply@ihre-domain.de" 
                          />
                          <p className="text-xs text-slate-500 mt-1">E-Mail die als Absender angezeigt wird</p>
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={contactSettings.smtp_use_tls !== false}
                              onChange={(e) => setContactSettings({...contactSettings, smtp_use_tls: e.target.checked})}
                              className="w-4 h-4 text-purple-600 rounded"
                            />
                            <span className="font-semibold">TLS verwenden (empfohlen)</span>
                          </label>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4">
                        <p className="text-sm text-blue-800">
                          <strong>💡 IONOS Hinweis:</strong> Verwenden Sie Ihre vollständige E-Mail-Adresse und das zugehörige Passwort. 
                          Standard Port 587 mit TLS funktioniert für die meisten IONOS Konten.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold mb-4">Google Ads Tracking</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Google Ads ID</Label>
                        <Input value={contactSettings.google_ads_id || ''} onChange={(e) => setContactSettings({...contactSettings, google_ads_id: e.target.value})} placeholder="z.B. AW-123456789" />
                        <p className="text-xs text-slate-500 mt-1">Format: AW-XXXXXXXXXX</p>
                      </div>
                      <div>
                        <Label>Conversion Label</Label>
                        <Input value={contactSettings.google_ads_conversion_label || ''} onChange={(e) => setContactSettings({...contactSettings, google_ads_conversion_label: e.target.value})} placeholder="Optional" />
                      </div>
                    </div>
                  </div>

                  <Button onClick={saveContactSettings} disabled={saving} className="w-full">
                    <Save className="mr-2" size={18} />
                    Kontaktdaten Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Impressum Tab */}
            <TabsContent value="impressum">
              <Card>
                <CardHeader>
                  <CardTitle>Impressum</CardTitle>
                  <CardDescription>Bearbeiten Sie die Impressum-Angaben nach §5 TMG</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* AGB Download Link Section */}
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-3 text-blue-900">📄 AGB Download-Link</h3>
                    <div>
                      <Label htmlFor="agb_download_url">AGB-Dokument URL</Label>
                      <Input 
                        id="agb_download_url"
                        value={impressumContent.agb_download_url || ''} 
                        onChange={(e) => setImpressumContent({...impressumContent, agb_download_url: e.target.value})} 
                        placeholder="https://example.com/documents/agb.pdf" 
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Dieser Link wird im Impressum als Download-Button für die AGBs angezeigt.
                        {impressumContent.agb_download_url && (
                          <span className="block mt-1 text-blue-600">
                            ✓ Vorschau: <a href={impressumContent.agb_download_url} target="_blank" rel="noopener noreferrer" className="underline">Link testen</a>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Unternehmen</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Firmenname</Label>
                        <Input value={impressumContent.company_name || ''} onChange={(e) => setImpressumContent({...impressumContent, company_name: e.target.value})} placeholder="z.B. Speier Solarbetriebe" />
                      </div>
                      <div>
                        <Label>Rechtsform</Label>
                        <Input value={impressumContent.company_form || ''} onChange={(e) => setImpressumContent({...impressumContent, company_form: e.target.value})} placeholder="z.B. GmbH" />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Adresse</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Straße und Hausnummer</Label>
                        <Input value={impressumContent.address_line1 || ''} onChange={(e) => setImpressumContent({...impressumContent, address_line1: e.target.value})} placeholder="z.B. Musterstraße 123" />
                      </div>
                      <div>
                        <Label>Adresszusatz (optional)</Label>
                        <Input value={impressumContent.address_line2 || ''} onChange={(e) => setImpressumContent({...impressumContent, address_line2: e.target.value})} placeholder="z.B. 2. Stock" />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>PLZ</Label>
                          <Input value={impressumContent.postal_code || ''} onChange={(e) => setImpressumContent({...impressumContent, postal_code: e.target.value})} placeholder="12345" />
                        </div>
                        <div className="col-span-2">
                          <Label>Stadt</Label>
                          <Input value={impressumContent.city || ''} onChange={(e) => setImpressumContent({...impressumContent, city: e.target.value})} placeholder="München" />
                        </div>
                      </div>
                      <div>
                        <Label>Land</Label>
                        <Input value={impressumContent.country || ''} onChange={(e) => setImpressumContent({...impressumContent, country: e.target.value})} placeholder="Deutschland" />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Kontakt</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>E-Mail</Label>
                        <Input value={impressumContent.email || ''} onChange={(e) => setImpressumContent({...impressumContent, email: e.target.value})} placeholder="info@example.com" />
                      </div>
                      <div>
                        <Label>Telefon</Label>
                        <Input value={impressumContent.phone || ''} onChange={(e) => setImpressumContent({...impressumContent, phone: e.target.value})} placeholder="+49 89 1234567" />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Rechtliche Angaben</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label>Geschäftsführer</Label>
                        <Input value={impressumContent.managing_director || ''} onChange={(e) => setImpressumContent({...impressumContent, managing_director: e.target.value})} placeholder="Max Mustermann" />
                      </div>
                      <div>
                        <Label>Registergericht</Label>
                        <Input value={impressumContent.registration_court || ''} onChange={(e) => setImpressumContent({...impressumContent, registration_court: e.target.value})} placeholder="z.B. Amtsgericht München" />
                      </div>
                      <div>
                        <Label>Registernummer</Label>
                        <Input value={impressumContent.registration_number || ''} onChange={(e) => setImpressumContent({...impressumContent, registration_number: e.target.value})} placeholder="HRB 12345" />
                      </div>
                      <div>
                        <Label>Umsatzsteuer-ID</Label>
                        <Input value={impressumContent.vat_id || ''} onChange={(e) => setImpressumContent({...impressumContent, vat_id: e.target.value})} placeholder="DE123456789" />
                      </div>
                    </div>
                  </div>

                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4">Verantwortlich für Inhalte (§55 Abs. 2 RStV)</h3>
                    <div>
                      <Label>Name</Label>
                      <Input value={impressumContent.responsible_person || ''} onChange={(e) => setImpressumContent({...impressumContent, responsible_person: e.target.value})} placeholder="Max Mustermann" />
                    </div>
                  </div>

                  <div>
                    <Label>Zusätzliche Informationen</Label>
                    <Textarea value={impressumContent.additional_info || ''} onChange={(e) => setImpressumContent({...impressumContent, additional_info: e.target.value})} rows={5} placeholder="Weitere rechtliche Hinweise, Haftungsausschluss, etc." />
                  </div>

                  <Button onClick={saveImpressum} disabled={saving} className="w-full">
                    <Save className="mr-2" size={18} />
                    Impressum Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Project Form Texts Tab */}
            <TabsContent value="project_form">
              <Card>
                <CardHeader>
                  <CardTitle>Projekt-Formular Texte</CardTitle>
                  <CardDescription>
                    Verwalten Sie alle Beschriftungen und Hilfetexte im Projekt-Bearbeitungsformular
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Grundinformationen */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-slate-800">Grundinformationen</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Projekttitel</Label>
                        <Input
                          value={projectFormTexts.label_title}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_title: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Standort</Label>
                        <Input
                          value={projectFormTexts.label_location}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_location: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Beschreibung</Label>
                        <Input
                          value={projectFormTexts.label_description}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Kapazität (MWh)</Label>
                        <Input
                          value={projectFormTexts.label_capacity_mw}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_capacity_mw: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Rendite p.a. (%)</Label>
                        <Input
                          value={projectFormTexts.label_expected_return}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_expected_return: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Laufzeit (Jahre)</Label>
                        <Input
                          value={projectFormTexts.label_duration_years}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_duration_years: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Anteile & Preis */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-slate-800">Anteile & Preis</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Gesamte Anteile</Label>
                        <Input
                          value={projectFormTexts.label_total_shares}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_total_shares: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Preis pro Anteil (€)</Label>
                        <Input
                          value={projectFormTexts.label_price_per_share}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_price_per_share: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Verfügbare Anteile</Label>
                        <Input
                          value={projectFormTexts.label_available_shares}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_available_shares: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Input
                          value={projectFormTexts.label_status}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_status: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Zahlungsstufen */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-slate-800">Zahlungsstufen</h3>
                    <div className="space-y-3">
                      <div>
                        <Label>Sektions-Überschrift</Label>
                        <Input
                          value={projectFormTexts.section_payment_stages}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, section_payment_stages: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <Label>Stufe 1</Label>
                          <Input
                            value={projectFormTexts.label_stage_1}
                            onChange={(e) => setProjectFormTexts({...projectFormTexts, label_stage_1: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Stufe 2</Label>
                          <Input
                            value={projectFormTexts.label_stage_2}
                            onChange={(e) => setProjectFormTexts({...projectFormTexts, label_stage_2: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Stufe 3</Label>
                          <Input
                            value={projectFormTexts.label_stage_3}
                            onChange={(e) => setProjectFormTexts({...projectFormTexts, label_stage_3: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Stufe 4</Label>
                          <Input
                            value={projectFormTexts.label_stage_4}
                            onChange={(e) => setProjectFormTexts({...projectFormTexts, label_stage_4: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Beschreibung Label</Label>
                        <Input
                          value={projectFormTexts.label_stage_description}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_stage_description: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Hilfetext Zahlungsstufen</Label>
                        <Input
                          value={projectFormTexts.help_payment_stages}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, help_payment_stages: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bankverbindung */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-slate-800">Bankverbindung</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Sektions-Überschrift</Label>
                        <Input
                          value={projectFormTexts.section_bank_details}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, section_bank_details: e.target.value})}
                        />
                      </div>
                      <div></div>
                      <div>
                        <Label>IBAN</Label>
                        <Input
                          value={projectFormTexts.label_iban}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_iban: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>BIC</Label>
                        <Input
                          value={projectFormTexts.label_bic}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_bic: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Kontoinhaber</Label>
                        <Input
                          value={projectFormTexts.label_account_holder}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_account_holder: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Bankname</Label>
                        <Input
                          value={projectFormTexts.label_bank_name}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_bank_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Verwendungszweck</Label>
                        <Input
                          value={projectFormTexts.label_purpose}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, label_purpose: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Hilfetext Verwendungszweck</Label>
                        <Input
                          value={projectFormTexts.help_purpose}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, help_purpose: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Buttons & Messages */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-slate-800">Buttons & Meldungen</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Speichern Button</Label>
                        <Input
                          value={projectFormTexts.button_save}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, button_save: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Zurück Button</Label>
                        <Input
                          value={projectFormTexts.button_back}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, button_back: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Erfolgs-Meldung</Label>
                        <Input
                          value={projectFormTexts.msg_save_success}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, msg_save_success: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Fehler-Meldung</Label>
                        <Input
                          value={projectFormTexts.msg_save_error}
                          onChange={(e) => setProjectFormTexts({...projectFormTexts, msg_save_error: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  <Button onClick={saveProjectFormTexts} disabled={saving} className="w-full">
                    <Save className="mr-2" size={18} />
                    Projekt-Formular Texte Speichern
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminContentNew;
